import { AuthContext, BaseButton, FileCabinetScreen } from '@enter/core';
import { mdiUpload } from '@mdi/js';
import Icon from '@mdi/react';
import { DocumentUploadDialog } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { DialogContext } from 'services';
import { IInjectedRootStore } from 'stores';
import { createProviderFileCabinetStore, FILE_CABINET_FETCH_GROUP } from './file-cabinet.store';
import * as S from './file-cabinet.styles';

/**
 * The provider app's File Cabinet (FE-225): the shared core screen on this app's API.
 *
 * The store lives with the page, as the Roster's does: built when the page opens, disposed when it
 * closes — after which it requests nothing, so the fetch group below cannot be switched back on by a
 * table fetch still waiting out its debounce.
 */
export const FileCabinet = inject('rootStore')(
    observer((props: {}): React.ReactElement => {
    const { rootStore } = props as IInjectedRootStore;
    // Held in a ref so the effect runs once per visit. The auth context is a new object on every
    // render of its provider, and an effect keyed on it would run its cleanup — aborting this page's
    // requests and resetting the fetch group — whenever an error dialog opens anywhere (ai-screen.tsx).
    const auth = useContext(AuthContext);
    const authRef = useRef(auth);
    authRef.current = auth;

    // Whether the page is open. A request that starts after it closed (one chained on a cancelled one)
    // must not switch the fetch group — and its error-dialog skip — back on for whatever page is next.
    const open = useRef(false);
    const [store] = useState(() =>
        createProviderFileCabinetStore(rootStore, () => {
            if (open.current) {
                authRef.current.setGroup(FILE_CABINET_FETCH_GROUP, { skipErrorHandler: true });
            }
        }),
    );

    // Re-armed on every mount: a hot reload re-runs effects but keeps the store from `useState`. A layout
    // effect, so it runs before the screen's and the table's effects make their first requests.
    useLayoutEffect(() => {
        open.current = true;
        store.activate();
    }, [store]);

    useEffect(() => {
        document.title = 'ENTER Platform - File Cabinet';
        return (): void => {
            open.current = false;
            store.dispose();
            authRef.current.cancelGroup(FILE_CABINET_FETCH_GROUP);
            // Back to the app's default group, error dialog on. The context's `group` is a snapshot from
            // the provider's last render, not what the previous page set, so it cannot be "restored";
            // every page that needs a group of its own sets it before its requests.
            authRef.current.setGroup('', {});
        };
    }, [store]);

    const dialogContext = useContext(DialogContext);
    // This app's existing upload: its dialog (types, claim, description), batch-upload store and demo
    // guard — the one the Documents page uses. Its batch can finish after the dialog closed, so the
    // cabinet refreshes where the user is; after the page closed, this visit's disposed store ignores it.
    const openUpload = (): void => {
        // An open drawer sits above the app's dialogs (it has to clear the table's own overlays) and covers
        // the right of the page: close it, so the upload dialog is never partly under it (PR #671 review).
        store.closeDrawer();
        const close = (): void => dialogContext.hideDialog();
        dialogContext.showDialog({
            props: { onClose: close, renderTitle: 'Upload Document' },
            Component: <DocumentUploadDialog onClose={close} onUploaded={(): void => store.refresh()} />,
        });
    };

    return (
        <FileCabinetScreen
            store={store}
            headerActions={
                <BaseButton variant="primary" size="compact" onClick={openUpload}>
                    <S.ButtonContent>
                        <Icon path={mdiUpload} size={0.7} />
                        Upload
                    </S.ButtonContent>
                </BaseButton>
            }
        />
    );
}),
);
